/**
 * UTM tracking utilities.
 *
 * Behaviour:
 *   • On every visit that carries at least one utm_* query param → persist
 *     the values in localStorage so they survive across route navigation.
 *   • At form submit time, `getUtmPayload()` returns a flat object with the
 *     5 UTM keys mapped to GHL: utm_source, utm_medium, utm_campaign,
 *     utm_adset, utm_content.
 */
const STORAGE_KEY = "lovac_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_adset", "utm_content"];

function readStorage() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStorage(data) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // storage unavailable (private mode, quota) — silently give up
  }
}

/**
 * Called on app boot and on every route change. Only writes when the current
 * URL carries utm_* params, so internal navigations are safe no-ops.
 */
export function initUtmTracking() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const now = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v && v.trim()) now[k] = v.trim();
  }
  // Meta/Facebook sometimes uses utm_term for the adset — canonicalise.
  if (!now.utm_adset) {
    const term = params.get("utm_term");
    if (term && term.trim()) now.utm_adset = term.trim();
  }
  if (Object.keys(now).length === 0) return;
  const stored = readStorage();
  writeStorage({ ...stored, ...now });
}

/**
 * Returns a flat payload ready to be sent to the backend / forwarded to GHL.
 * All 5 keys are always present (empty string if never seen).
 */
export function getUtmPayload() {
  const stored = readStorage();
  const out = {};
  for (const k of UTM_KEYS) out[k] = stored[k] || "";
  return out;
}
